<template>
  <Teleport to="body">
    <Transition
      name="c-lightbox"
      @after-enter="onAfterEnter"
    >
      <div
        v-if="isOpen"
        :class="b()"
      >
        <slot></slot>
        <button
          :class="b('button', { close: true })"
          :aria-label="$t('c-lightbox.button-close-aria')"
          type="button"
          @click="close"
        >
          <e-icon
            icon="i-close"
            size="40"
            aria-hidden="true"
          />
        </button>
        <button
          :class="b('button', { previous: true })"
          :aria-label="$t('c-lightbox.button-prev-aria')"
          :disabled="disablePrevious"
          type="button"
          @click="previous"
        >
          <e-icon
            icon="i-chevron--left"
            size="40"
            aria-hidden="true"
          />
        </button>
        <button
          :class="b('button', { next: true })"
          :aria-label="$t('c-lightbox.button-next-aria')"
          :disabled="disableNext"
          type="button"
          @click="next"
        >
          <e-icon
            icon="i-chevron--right"
            size="40"
            aria-hidden="true"
          />
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import eIcon from '@/elements/e-icon.vue';

  // type Setup = {};
  // type Data = {};

  /**
   * Renders a Lightbox.
   */
  export default defineComponent({
    name: 'c-lightbox',

    components: {
      eIcon,
    },

    props: {
      /**
       * Toggles the visibility of the lightbox.
       */
      isOpen: {
        type: Boolean,
        default: false,
      },

      /**
       * Defines if the "next"-button is displayed.
       */
      disableNext: {
        type: Boolean,
        default: false,
      },

      /**
       * Defines if the "previous"-button is displayed.
       */
      disablePrevious: {
        type: Boolean,
        default: false,
      },
    },
    emits: {
      'update:isOpen': (state: unknown): boolean => typeof state === 'boolean',
      'next': (): boolean => true,
      'previous': (): boolean => true,
    },

    // setup(): Setup {},
    // data(): Data {
    //   return {};
    // },

    // computed: {},
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      document.removeEventListener('keydown', this.onKeyDown);
    },
    // unmounted() {},

    methods: {
      /**
       * Handles the after-enter event of the lightbox.
       */
      onAfterEnter(): void {
        this.$bodyscroll.disable(this.$el as HTMLElement);
        document.addEventListener('keydown', this.onKeyDown);
      },

      /**
       * Handles keydown events.
       */
      onKeyDown(event: KeyboardEvent): void {
        if (!this.isOpen) {
          return;
        }

        switch (event.code) {
          case 'Escape':
            this.close();
            break;

          case 'ArrowLeft':
            this.previous();
            break;

          case 'ArrowRight':
            this.next();
            break;

          // no default
        }
      },

      /**
       * Closes the lightbox.
       */
      close() {
        this.$bodyscroll.enable(this.$el as HTMLElement);
        document.removeEventListener('keydown', this.onKeyDown);
        this.$emit('update:isOpen', false);
      },

      /**
       * Emits "next"-Event.
       */
      next(): void {
        if (this.disableNext) {
          return;
        }

        this.$emit('next');
      },

      /**
       * Emits "previous"-Event.
       */
      previous(): void {
        if (this.disablePrevious) {
          return;
        }

        this.$emit('previous');
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .c-lightbox {
    @include mixins.z-index(modal);

    &,
    &::after {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
    }

    &::after {
      @include mixins.z-index(back);

      content: '';
      background-color: variables.$va-color-grayscale--1000;
      opacity: 0.95;
    }

    &__button {
      position: absolute;
      color: variables.$va-color-grayscale--0;
      cursor: pointer;
    }

    &__button--next,
    &__button--previous {
      top: 60px;
      bottom: 0;
      width: 50vw;

      .e-icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }

      &[disabled] {
        display: none;
      }
    }

    &__button--next {
      right: 0;

      .e-icon {
        right: 15px;
      }
    }

    &__button--previous {
      left: 0;

      .e-icon {
        left: 25px;
      }
    }

    &__button--close {
      top: 15px;
      right: 15px;
    }

    img {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      max-width: 85vw;
      max-height: 85vh;
      object-fit: contain;
    }

    figcaption {
      position: absolute;
      bottom: 20px;
      left: 0;
      width: 100%;
      text-align: center;
      color: variables.$va-color-grayscale--0;
    }
  }

  .c-lightbox-transition-enter-active,
  .c-lightbox-transition-leave-active {
    transition: opacity variables.$va-transition-duration--100 ease-in-out;
  }

  .c-lightbox-transition-enter-from,
  .c-lightbox-transition-leave-to {
    opacity: 0;
  }
</style>
